import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { useConfig } from "../../../context/ConfigContext";
import GuateIMG from "../../../components/img/Guatemala.svg";
import { formatQTZ } from "../../../components/utils/format";
import { roundTo } from "../../../components/utils/mathUtils";
import {
    calcularPanelesNecesarios,
    calcularSistemaDesdePanel,
} from "../../../components/utils/infoPanel";
import { logEvent } from "../../../lib/analytics";

const InitialDataStep = ({ onDataSubmit, initialData }) => {
    const config = useConfig();
    const {
        guatemalaRegions,
        KWH_DIA_PANEL,
        COSTO_ENERGIA_PROM_SINPANEL,
        GARANTY_YEARS,
        DESCUENTO_CONTADO,
    } = config;

    // Preseleccionar Región 1 (Metropolitana) por defecto
    const defaultRegion = guatemalaRegions?.find((r) => r.id === 1) || guatemalaRegions?.[0];

    const [selectedZone, setSelectedZone] = useState(
        initialData?.location?.zone || defaultRegion?.id || 1
    );
    const [inputMethod, setInputMethod] = useState(
        initialData?.consumption?.method || "manual"
    );
    const [monthlyBill, setMonthlyBill] = useState(
        initialData?.consumption?.monthlyBill || ""
    );
    const [monthlyKwh, setMonthlyKwh] = useState(
        initialData?.consumption?.monthlyKwh || ""
    );
    const [propertyType, setPropertyType] = useState(
        initialData?.consumption?.propertyType || ""
    );
    const [validationError, setValidationError] = useState("");

    const propertyTypes = [
        { id: "apartment", name: "Apartamento", avgKwh: 250, icon: "Building2" },
        { id: "small-house", name: "Casa Pequeña", avgKwh: 350, icon: "Home" },
        { id: "medium-house", name: "Casa Mediana", avgKwh: 500, icon: "Home" },
        { id: "large-house", name: "Casa Grande", avgKwh: 750, icon: "Home" },
        { id: "commercial", name: "Comercial/Oficina", avgKwh: 1200, icon: "Building" },
    ];

    const handleZoneSelect = (zone) => {
        setSelectedZone(zone?.id);
    };

    const handlePropertyTypeSelect = (type) => {
        setPropertyType(type?.id);
        setMonthlyKwh(type?.avgKwh);
        setMonthlyBill("");
    };

    const devuelveInfoPlan = (noPanel, monthlyKwh) => {
        const generaPanel = roundTo(KWH_MES_PANEL * noPanel);
        const coberturaPanel = roundTo(generaPanel / monthlyKwh);

        return {
            panel: noPanel,
            energia: generaPanel,
            porcentaje: coberturaPanel,
            porce100: roundTo(coberturaPanel * 100),
        };
    };

    const handleSubmit = () => {
        setValidationError("");

        // Validar que haya región seleccionada
        if (!selectedZone) {
            setValidationError("Por favor selecciona tu región");
            return;
        }

        // Validar consumo
        let finalMonthlyKwh = 0;
        if (inputMethod === "manual") {
            if (!monthlyBill || parseFloat(monthlyBill) <= 0) {
                setValidationError("Por favor ingresa tu factura mensual");
                return;
            }
            const billAmount = parseFloat(monthlyBill);
            finalMonthlyKwh = billAmount;
        } else {
            if (!propertyType) {
                setValidationError("Por favor selecciona el tipo de propiedad");
                return;
            }
            finalMonthlyKwh = monthlyKwh;
        }

        // Calcular sistema
        const selectedRegion = guatemalaRegions?.find((r) => r.id === selectedZone);
        const resultPaneles = calcularPanelesNecesarios(finalMonthlyKwh, config);

        if (resultPaneles.error) {
            setValidationError(resultPaneles.message);
            return;
        }

        const systemCalculation = calcularSistemaDesdePanel(
            resultPaneles.panelesNecesarios,
            selectedZone,
            finalMonthlyKwh,
            config
        );

        // Google Analytics Event
        logEvent(
            "Calculadora Solar",
            "Ver_resultados",
            inputMethod === "manual" ? "Manual" : "Estimado",
            parseFloat(finalMonthlyKwh)
        );

        // Preparar datos para enviar
        const locationData = {
            zone: selectedZone,
            zoneName: selectedRegion?.name,
            solarPotential: KWH_DIA_PANEL * selectedRegion?.porcentaje,
        };

        const consumptionData = {
            method: inputMethod,
            monthlyBill: inputMethod === "manual" ? parseFloat(monthlyBill) : null,
            monthlyKwh: finalMonthlyKwh,
            propertyType: inputMethod === "estimate" ? propertyType : null,
            estimatedCost: roundTo(finalMonthlyKwh * COSTO_ENERGIA_PROM_SINPANEL),
        };

        // Calcular valores financieros adicionales
        const inversionSinDescuento = systemCalculation.inversionTotal / (1 - DESCUENTO_CONTADO / 100);
        const totalSavings25Years = systemCalculation.yearlySavings * GARANTY_YEARS;
        const roiPercent = roundTo((totalSavings25Years / systemCalculation.inversionTotal) * 100);


        const systemDesign = {
            panels: resultPaneles.panelesNecesarios,
            systemSize: systemCalculation.energiaProducida,
            monthlyGeneration: systemCalculation.energiaRequerida,
            monthlySavings: systemCalculation.monthlySavings,
            monthlyBillAfter: systemCalculation.monthlyBillAfter,
            yearlySavings: systemCalculation.yearlySavings,
            paybackYears: systemCalculation.paybackPeriod,
            investment: inversionSinDescuento, // Precio original sin descuento
            coverage: systemCalculation.porcentajeCubierto,
            roiPercent: roiPercent,
        };

        const financingData = {
            type: "cash",
            selectedPlan: "cash",
            desContado: DESCUENTO_CONTADO,
            cashPrice: inversionSinDescuento,
            totalCost: systemCalculation.inversionTotal, // Precio con descuento (inversión neta)
            discount: DESCUENTO_CONTADO,
            totalSavings25Years: totalSavings25Years,
        };

        onDataSubmit({
            location: locationData,
            consumption: consumptionData,
            systemDesign: systemDesign,
            financing: financingData,
        });
    };

    return (
        <div className="space-y-6">
            <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-foreground mb-2">
                    Información Inicial
                </h2>
                <p className="text-muted-foreground">
                    Selecciona tu región e ingresa tu consumo energético
                </p>
            </div>

            {/* Layout de 2 columnas */}
            <div className="grid lg:grid-cols-2 gap-6">
                {/* Columna Izquierda: Mapa de Regiones (Compacto) */}
                <div className="bg-gradient-to-b from-blue-50 to-green-50 rounded-xl p-6 border">
                    <h3 className="text-lg font-semibold text-foreground mb-3 text-center">
                        Tu Región
                    </h3>

                    {/* Mapa más pequeño */}
                    <div className="relative mx-auto w-64 aspect-[4/4] rounded-lg border-2 border-gray-200 overflow-hidden mb-4">
                        <img
                            src={GuateIMG}
                            alt="Mapa de Guatemala"
                            className="absolute inset-0 w-full h-full object-contain"
                        />

                        {/* Region Markers */}
                        {guatemalaRegions?.map((region) => (
                            <button
                                key={region?.id}
                                className={`absolute w-3 h-3 rounded-full border-2 border-white shadow-lg transition-all duration-300 hover:scale-125 ${selectedZone === region?.id
                                    ? "bg-orange-500 scale-125"
                                    : "bg-green-600 hover:bg-green-700"
                                    }`}
                                style={{
                                    left: region?.coordinates?.x,
                                    top: region?.coordinates?.y,
                                    transform: "translate(-50%, -50%)",
                                }}
                                onClick={() => handleZoneSelect(region)}
                                title={region?.label}
                            />
                        ))}
                    </div>

                    {/* Info de región seleccionada */}
                    {selectedZone && (
                        <div className="bg-white rounded-lg p-4 text-sm">
                            <div className="font-semibold text-foreground mb-1">
                                {guatemalaRegions?.find((r) => r.id === selectedZone)?.name}
                            </div>
                            <div className="text-muted-foreground text-s">
                                {guatemalaRegions?.find((r) => r.id === selectedZone)?.label}
                            </div>
                            <div className="text-muted-foreground text-xs">
                                Potencial: {roundTo((KWH_DIA_PANEL * guatemalaRegions?.find((r) => r.id === selectedZone)?.porcentaje) / 100)} kWh/día por panel
                            </div>
                        </div>
                    )}
                </div>

                {/* Columna Derecha: Consumo Energético (Compacto) */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-foreground text-center">
                        Tu Consumo
                    </h3>

                    {/* Método de ingreso */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => setInputMethod("manual")}
                            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${inputMethod === "manual"
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-muted-foreground hover:bg-muted/80"
                                }`}
                        >
                            Factura Mensual
                        </button>
                        <button
                            onClick={() => setInputMethod("estimate")}
                            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${inputMethod === "estimate"
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-muted-foreground hover:bg-muted/80"
                                }`}
                        >
                            Estimar
                        </button>
                    </div>

                    {/* Input Manual */}
                    {inputMethod === "manual" && (
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Factura Mensual Promedio (kWh)
                            </label>
                            <Input
                                type="number"
                                value={monthlyBill}
                                onChange={(e) => {
                                    setMonthlyBill(e.target.value);
                                    setValidationError("");
                                }}
                                placeholder="Ej: 450"
                                className="w-full"
                            />
                            {monthlyBill && (
                                <p className="text-xs text-muted-foreground mt-1">
                                    Costo estimado: {formatQTZ(parseFloat(monthlyBill) * COSTO_ENERGIA_PROM_SINPANEL * 1.13)}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Input por Estimación */}
                    {inputMethod === "estimate" && (
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Tipo de Propiedad
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                {propertyTypes.map((type) => (
                                    <button
                                        key={type.id}
                                        onClick={() => handlePropertyTypeSelect(type)}
                                        className={`p-3 rounded-lg border-2 transition-all text-left ${propertyType === type.id
                                            ? "border-primary bg-primary/5"
                                            : "border-border hover:border-primary/50"
                                            }`}
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                            <Icon name={type.icon} size={16} className="text-primary" />
                                            <span className="text-xs font-medium">{type.name}</span>
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            ~{type.avgKwh} kWh/mes
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Error de validación */}
                    {validationError && (
                        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                            <p className="text-sm text-destructive flex items-center gap-2">
                                <Icon name="AlertCircle" size={16} />
                                {validationError}
                            </p>
                        </div>
                    )}

                    {/* Botón de continuar */}
                    <Button
                        onClick={handleSubmit}
                        className="w-full"
                        size="lg"
                        iconName="ArrowRight"
                        iconPosition="right"
                    >
                        Ver Resultados
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default InitialDataStep;
